import { userAvatarBucketName } from '@/config/env';
import type { User } from '@/features/participant';
import { GradientAvatar } from '@/shared/components';
import { cn } from '@/shared/utilities';
import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MAX_MEMBERS } from '../constants';

export interface UserListSelectedProps {
  selects: User[];
  onRemove: (user: User) => void;
  maxHeight?: number | string;
  className?: string;
}

export const UserListSelected: React.FC<UserListSelectedProps> = ({
  selects,
  onRemove,
  maxHeight = 200,
  className
}) => {
  const { t } = useTranslation();

  if (selects.length === 0) {
    return (
      <Box className={cn('mex:py-4 mex:text-center', className)}>
        <Typography variant='body2' className='mex:text-gray-500'>
          {t('conversations.validation.membersRequired')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack className={cn('mex:gap-2 mex:space-y-2', className)}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' className='mex:!mb-2'>
        <Typography variant='subtitle2' className='mex:!font-medium'>
          {t('conversations.members.addButton')}
        </Typography>
        <Typography variant='body2' color='primary'>
          {t('conversations.validation.membersRequired')}: {selects.length}/{MAX_MEMBERS}
        </Typography>
      </Stack>
      <Box className='mex:flex mex:flex-wrap mex:gap-2 mex:overflow-auto' sx={{ maxHeight }}>
        {selects.map((user) => (
          <Chip
            key={user.id}
            avatar={
              <GradientAvatar
                peerId={user.id}
                src={user.profile.avatarRef || undefined}
                name={user.fullName}
                bucketName={userAvatarBucketName}
              />
            }
            label={user.fullName}
            onDelete={() => onRemove(user)}
            deleteIcon={<CloseIcon className='mex:!w-4 mex:!h-4' />}
            className='mex:!bg-primary/10 mex:!text-primary mex:hover:!bg-primary/20'
            size='small'
          />
        ))}
      </Box>
    </Stack>
  );
};
